import * as Yup from 'yup';
import type { AnyObject, Flags, Maybe } from 'yup';
import { differenceInCalendarDays } from './date';

export const getYupFieldLabels = (validationSchema: Yup.AnySchema): Map<string, string> => {
	const labels = new Map<string, string>();
	const traverseSchema = (schema: Yup.AnySchema, path = '') => {
		if (schema.spec?.label) {
			labels.set(path, schema.spec.label);
		}
		if (schema instanceof Yup.ObjectSchema) {
			const fields = schema.fields;
			for (const key in fields) {
				const field = fields[key] as Yup.AnySchema;
				const fieldPath = path ? `${path}.${key}` : key;
				traverseSchema(field, fieldPath);
			}
		} else if (schema instanceof Yup.ArraySchema) {
			const innerSchema = schema.innerType as Yup.AnySchema | undefined;
			if (innerSchema) {
				traverseSchema(innerSchema, `${path}[]`);
			}
		}
	};

	traverseSchema(validationSchema);
	return labels;
};

export const getYupRequiredFields = (validationSchema: Yup.AnySchema): string[] => {
	const requiredFields: string[] = [];
	const traverseSchema = (schema: Yup.AnySchema, path = '') => {
		if (schema.spec && !schema.spec.optional && !schema.spec.nullable) {
			requiredFields.push(path);
		}

		if (schema instanceof Yup.ObjectSchema) {
			const fields = schema.fields;
			for (const key in fields) {
				const field = fields[key] as Yup.AnySchema;
				const fieldPath = path ? `${path}.${key}` : key;
				traverseSchema(field, fieldPath);
			}
		} else if (schema instanceof Yup.ArraySchema) {
			const innerSchema = schema.innerType as Yup.AnySchema | undefined;
			if (innerSchema) {
				traverseSchema(innerSchema, `${path}[]`);
			}
		}
	};

	traverseSchema(validationSchema);
	return requiredFields;
};

Yup.setLocale({
	mixed: {
		required: ({ label }) => (label ? `${label.replace(' *', '')} is required.` : 'Field is required.')
	},
	string: {
		matches: '${label} is invalid.',
		email: '${label} is not a valid email.',
		url: '${label} is not a valid URL.',
		max: '${label} must be at most ${max} characters.'
	},
	array: {
		min: ({ min, label }) => (min === 0 ? `${label} must be empty` : `${label} must have at least ${min} selection.`),
		max: ({ max, label }) => (max === 0 ? `${label} must be empty` : `${label} must have at most ${max} selections.`)
	}
});

Yup.addMethod<Yup.AnyObjectSchema>(Yup.object, 'address', function (label?: string) {
	return this.nullable()
		.shape({
			addressOne: Yup.string().label('Street Address').nullable(),
			aptSuiteNumber: Yup.string().label('Apt/Ste #').nullable(),
			city: Yup.string().label('City').nullable(),
			state: Yup.string().label('State').nullable(),
			zipCode: Yup.string().zipCode().label('Zip').nullable(),
			zipCodeExtension: Yup.string().label('Ext').nullable(),
			county: Yup.string().label('County').nullable(),
			mapIntegrationId: Yup.string().nullable(),
			notes: Yup.string().label('Notes').nullable()
		})
		.label(label ?? 'Address');
});

Yup.addMethod(Yup.string, 'otherReason', function (selectName: string, otherOptionValue: number, errorMessage?: string) {
	return this.when(selectName, ([selectValue], schema) => {
		if (Number(String(selectValue)) === otherOptionValue) {
			return schema.required(errorMessage ?? 'Other response required');
		}
		return schema;
	});
});

Yup.addMethod(Yup.string, 'phoneNumber', function () {
	return this.matches(/^\d{10}$/, 'Phone number must be in the format (XXX) XXX-XXXX');
});

Yup.addMethod(Yup.string, 'monthYear', function () {
	return this.test('validate-month-year', 'Invalid date. Format MM/YYYY', (value: string | undefined) => {
		if (!value || value.length === 0) {
			return true;
		}
		if (value.length !== 6) {
			return false;
		}
		const month = parseInt(value.slice(0, 2), 10);
		const year = parseInt(value.slice(2), 10);
		return month >= 1 && month <= 12 && year > 0;
	});
});

Yup.addMethod(Yup.string, 'zipCode', function () {
	return this.test('validate-zip-code', 'Invalid zip code format', (value: string | undefined) => {
		if (!value) {
			return true;
		}
		const zipCodeRegex = /^\d{5}(?:[-\s]\d{4})?$/;
		return zipCodeRegex.test(value);
	});
});

Yup.addMethod(Yup.date, 'effectiveDate', function () {
	return this.test('validate-effective-date', 'Must be today or in future', (effectiveDate: Date | undefined) => {
		if (!effectiveDate) {
			return true;
		}
		return differenceInCalendarDays(effectiveDate, new Date()) >= 0;
	});
});

Yup.addMethod(Yup.date, 'discontinueDate', function () {
	return this.test('validate-discontinue-date', 'Must be today or in future', function (discontinueDate) {
		return !discontinueDate || differenceInCalendarDays(discontinueDate, new Date()) >= 0;
	}).test('discontinueGreaterThanOrEqualToEffective', 'Must be equal to or greater than Effective Date', function (discontinueDate) {
		const { effectiveDate } = this.parent;
		if (!effectiveDate || !discontinueDate) {
			return true;
		}
		return differenceInCalendarDays(effectiveDate, discontinueDate) <= 0;
	});
});

Yup.addMethod(Yup.string, 'npi', function () {
	return this.test('validate-npi', 'Invalid format', (value: string | undefined) => {
		if (!value) {
			return true;
		}
		const npiRegex = /^\d{10}$/;
		return npiRegex.test(value);
	});
});

Yup.addMethod(
	Yup.array,
	'mustBeOnlySelectedValue',
	function (specialValue: unknown, message: string = 'If ${path} contains the special value, it must only contain that value') {
		return this.test({
			name: 'mustBeOnlySelectedValue',
			message,
			test: function (value) {
				// Skip validation if value is not an array
				if (!value || !Array.isArray(value)) return true;

				// Check if array contains the special value
				if (value.includes(specialValue)) {
					// If it contains the special value, make sure it only contains that value
					return value.length === 1 && value[0] === specialValue;
				}

				// If special value is not in the array, validation passes
				return true;
			}
		});
	}
);

Yup.addMethod(Yup.string, 'mustEqual', function (value: string, message: string = '${path} must equal ' + value) {
	return this.test({
		name: 'mustEqual',
		message,
		test: function (val) {
			if (val === undefined || val === null) return true; // Skip validation if value is not set
			return val === value;
		}
	});
});

Yup.addMethod(Yup.number, 'mustEqual', function (value: string | number, message: string = '${path} must equal ' + value) {
	return this.test({
		name: 'mustEqual',
		message,
		test: function (val) {
			if (val === undefined || val === null) return true; // Skip validation if value is not set
			return val.toString() === value.toString();
		}
	});
});

declare module 'yup' {
	// Generic parameter lists must match yup's own declarations exactly, including their `any` defaults
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	interface ObjectSchema<TIn extends Maybe<AnyObject>, TContext = AnyObject, TDefault = any, TFlags extends Flags = ''> {
		address(label?: string): this;
	}

	interface StringSchema<TType extends Maybe<string> = string | undefined, TContext = AnyObject, TDefault = undefined, TFlags extends Flags = ''> {
		phoneNumber(): this;
		monthYear(): this;
		zipCode(): this;
		npi(): this;
		otherReason(selectName: string, otherOptionValue: number, errorMessage?: string): this;
		mustEqual(value: string, message?: string): this;
	}

	interface NumberSchema<TType extends Maybe<number> = number | undefined, TContext = AnyObject, TDefault = undefined, TFlags extends Flags = ''> {
		mustEqual(value: string | number, message?: string): this;
	}

	interface DateSchema<TType extends Maybe<Date>, TContext = AnyObject, TDefault = undefined, TFlags extends Flags = ''> {
		discontinueDate(): this;
		effectiveDate(): this;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	interface ArraySchema<TIn extends any[] | null | undefined, TContext, TDefault = undefined, TFlags extends Flags = ''> {
		mustBeOnlySelectedValue(specialValue: unknown, message?: string): this;
	}
}

export default Yup;
